import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../config/axiosInstance';
import ManageModal from '../components/ManageModal';
import LoginPasswordModal from '../components/LoginPasswordModal';
import FundPasswordModal from '../components/FundPasswordModal';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTrades: 0,
    totalBalance: 45678.9,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loginPasswordUser, setLoginPasswordUser] = useState(null);
  const [showFundPasswordModal, setShowFundPasswordModal] = useState(false);
  const [fundPasswordData, setFundPasswordData] = useState(null);
  const [loadingFundPassword, setLoadingFundPassword] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = recentUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(recentUsers.length / itemsPerPage);

  // Function to generate pagination numbers with ellipsis
  const getPaginationNumbers = () => {
    const pages = [];

    if (totalPages <= 3) {
      // Show all pages if 3 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Check if we're in the last 3 pages
      if (currentPage >= totalPages - 2) {
        // Show last 3 pages without ellipsis (e.g., 6,7,8)
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show current page and next page (2 consecutive)
        pages.push(currentPage);
        pages.push(currentPage + 1);
        // Add ellipsis
        pages.push('...');
        // Add last 2 pages
        pages.push(totalPages - 1);
        pages.push(totalPages);
      }
    }

    return pages;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axiosInstance.get('api/admin/dashboard');
        console.log(response.data);

        if (response.data.success && response.data.data) {
          const { stats, recentUsers } = response.data.data;

          // Update stats with real data from API
          setStats((prevStats) => ({
            ...prevStats,
            totalUsers: stats.users.total || 0,
            activeUsers: stats.users.active || 0,
            totalBalance: stats.wallets.totalBalance || 0,
          }));

          // Set recent users
          setRecentUsers(recentUsers);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchTradingStats = async () => {
      try {
        const response = await axiosInstance.get(
          'api/admin/trading/statistics'
        );

        if (response.data.success && response.data.data) {
          const { totalTrades } = response.data.data;
          setStats((prevStats) => ({
            ...prevStats,
            totalTrades: totalTrades || 0,
          }));
        }
      } catch (error) {
        console.error('Error fetching trading stats:', error);
      }
    };

    fetchDashboardData();
    fetchTradingStats();
  }, []);

  const openModal = (user) => {
    setSelectedUser(user);
  };

  const handleUpdateUser = (updatedUser) => {
    // Find the old user before updating
    const oldUser = recentUsers.find((u) => u.id === updatedUser.id);

    // Update the user list
    setRecentUsers((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? updatedUser : u))
    );

    // Update active users count only if status actually changed
    if (oldUser && oldUser.status !== updatedUser.status) {
      setStats((prevStats) => ({
        ...prevStats,
        activeUsers:
          updatedUser.status === 'ACTIVE'
            ? prevStats.activeUsers + 1
            : prevStats.activeUsers - 1,
      }));
    }

    setSelectedUser(updatedUser);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  const handleOpenFundPasswordModal = async (user) => {
    setShowFundPasswordModal(true);
    setLoadingFundPassword(true);
    setFundPasswordData(null);
    try {
      const response = await axiosInstance.get(
        `/api/admin/transaction-passwords?uid=${encodeURIComponent(user.uid)}`
      );
      if (response.data.success && response.data.data) {
        setFundPasswordData(response.data.data.user);
      }
    } catch (error) {
      console.error('Error fetching fund password:', error);
    } finally {
      setLoadingFundPassword(false);
    }
  };

  const handleCloseFundPasswordModal = () => {
    setShowFundPasswordModal(false);
    setFundPasswordData(null);
  };

  const handleUpdateFundPassword = async (newPassword) => {
    try {
      const response = await axiosInstance.put(
        `/api/admin/transaction-passwords/${fundPasswordData.uid}`,
        { newPassword }
      );
      if (response.data.success) {
        const refreshResponse = await axiosInstance.get(
          `/api/admin/transaction-passwords?uid=${fundPasswordData.uid}`
        );
        if (refreshResponse.data.success) {
          setFundPasswordData(refreshResponse.data.data.user);
        }
        return { success: true, message: 'Fund password updated successfully' };
      }
    } catch (error) {
      console.error('Error updating fund password:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update fund password',
      };
    }
    return { success: false, message: 'Failed to update fund password' };
  };

  return (
    <div className='container-fluid py-2 lg:py-5'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h2>Admin Dashboard</h2>
      </div>

      {/* Statistics Cards */}
      <div className='row mb-4 g-3'>
        <div className='col-12 col-md-6 col-lg-3'>
          <div className='card text-center bg-primary text-white'>
            <div className='card-body'>
              <h3>{loading ? '...' : stats.totalUsers}</h3>
              <p>Total Users</p>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-lg-3'>
          <div className='card text-center bg-success text-white'>
            <div className='card-body'>
              <h3>{loading ? '...' : stats.activeUsers}</h3>
              <p>Active Users</p>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-lg-3'>
          <div className='card text-center bg-info text-white'>
            <div className='card-body'>
              <h3>{loading ? '...' : stats.totalTrades}</h3>
              <p>Total Trades</p>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-lg-3'>
          <div className='card text-center bg-warning text-white'>
            <div className='card-body'>
              <h3>{loading ? '...' : `$${stats.totalBalance.toFixed(2)}`}</h3>
              <p>Total Balance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Card */}
      {/* <div className="card p-4 shadow-sm">
        <h5>Welcome to Crypto Admin Panel!</h5>
        <p>
          Manage users, control trades, and monitor your platform from this
          dashboard.
        </p>
        <hr />
        <div className="row">
          <div className="col-md-6">
            <h6>Quick Actions:</h6>
            <ul>
              <li>View and manage registered users</li>
              <li>Control trade outcomes</li>
              <li>Handle support requests</li>
              <li>Configure wallet settings</li>
            </ul>
          </div>
          <div className="col-md-6">
            <h6>Recent Activity:</h6>
            <ul>
              <li>15 new users registered today</li>
              <li>234 trades executed</li>
              <li>5 support tickets pending</li>
              <li>All systems operational</li>
            </ul>
          </div>
        </div>
      </div> */}

      {/* Recent Users Table */}
      <div className='card shadow-sm border-0'>
        <div className='card-header bg-white border-bottom py-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <h5 className='mb-0 fw-semibold'>Recent Users</h5>
            <button
              className='btn btn-primary btn-sm'
              onClick={() => navigate('/admin/users')}
            >
              View All Users
            </button>
          </div>
        </div>
        <div className='card-body p-0'>
          {loading ? (
            <div className='text-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-hover align-middle mb-0'>
                <thead className='table-light'>
                  <tr>
                    <th className='text-start ps-3 py-3'>UID</th>
                    <th className='text-start py-3'>Email</th>
                    <th className='text-start py-3'>Name</th>
                    <th className='text-start py-3'>Status</th>
                    <th className='text-start py-3'>KYC Status</th>
                    <th className='text-start py-3'>Joined</th>
                    <th className='text-start py-3'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.length > 0 ? (
                    currentUsers.map((user) => (
                      <tr
                        key={user.id}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td className='text-start ps-3 py-3'>{user.uid}</td>
                        <td className='text-start py-3'>{user.email}</td>
                        <td className='text-start py-3'>
                          {user.fullName || 'N/A'}
                        </td>
                        <td className='text-start py-3'>
                          <span
                            className={`badge ${
                              user.status === 'ACTIVE'
                                ? 'bg-success'
                                : 'bg-danger'
                            }`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className='text-start py-3'>
                          <span
                            className={`badge ${
                              user.kycStatus === 'APPROVED'
                                ? 'bg-success'
                                : user.kycStatus === 'PENDING'
                                ? 'bg-warning'
                                : 'bg-danger'
                            }`}
                          >
                            {user.kycStatus}
                          </span>
                        </td>
                        <td className='text-start py-3'>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className='text-start py-3'>
                          <button
                            className='btn btn-sm btn-primary rounded-pill px-3'
                            onClick={() => openModal(user)}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='7' className='text-center py-5 text-muted'>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Pagination */}
        {!loading && recentUsers.length > itemsPerPage && (
          <div className='card-footer bg-white border-top py-3'>
            <div className='d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2'>
              <div className='text-muted small text-center text-sm-start'>
                Showing {indexOfFirstItem + 1} to{' '}
                {Math.min(indexOfLastItem, recentUsers.length)} of{' '}
                {recentUsers.length} users
              </div>
              <nav>
                <ul className='pagination pagination-sm mb-0'>
                  <li
                    className={`page-item ${
                      currentPage === 1 ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>
                  </li>
                  {getPaginationNumbers().map((page, index) => {
                    if (page === '...') {
                      return (
                        <li
                          key={`ellipsis-${index}`}
                          className='page-item disabled'
                        >
                          <span className='page-link'>...</span>
                        </li>
                      );
                    }
                    return (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page ? 'active' : ''
                        }`}
                      >
                        <button
                          className='page-link'
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className='page-link'
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      <ManageModal
        selectedUser={selectedUser}
        onClose={handleCloseModal}
        onUpdate={handleUpdateUser}
        onChangeLoginPassword={setLoginPasswordUser}
        onOpenFundPassword={handleOpenFundPasswordModal}
      />

      <LoginPasswordModal
        user={loginPasswordUser}
        onClose={() => setLoginPasswordUser(null)}
      />

      <FundPasswordModal
        isOpen={showFundPasswordModal}
        onClose={handleCloseFundPasswordModal}
        fundPasswordData={fundPasswordData}
        loading={loadingFundPassword}
        onUpdate={handleUpdateFundPassword}
      />
    </div>
  );
};

export default AdminDashboard;
